import React from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import { useAuth } from '../contexts/AuthContext';

const PermissionGuard = ({ 
  children, 
  permission, 
  permissions, 
  requireAll = true, 
  module, 
  action, 
  fallback = null 
}) => {
  const { 
    hasPermission, 
    hasAnyPermission, 
    hasAllPermissions, 
    hasModulePermission,
    canAccessModule,
    loading 
  } = usePermissions();
  const { user } = useAuth();

  // Show loading state while permissions are being fetched
  if (loading) {
    return (
      <div data-testid="permission-loading" className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // If no user, don't render anything (should be handled by auth)
  if (!user) {
    return fallback || null;
  }

  // Admin users have access to everything
  if (user.role === 'admin') {
    return children;
  }

  // Single permission check
  if (permission) {
    if (!hasPermission(permission)) {
      return fallback || (
        <div data-testid="access-denied" className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-gray-900">Access Denied</h1>
            <p className="mt-2 text-gray-600">You don't have permission to access this resource.</p>
            <p data-testid="required-permission" className="mt-1 text-sm text-gray-500">Required permission: {permission}</p>
          </div>
        </div>
      );
    }
  }

  // Module and action check
  if (module && action) {
    if (!hasModulePermission(module, action)) {
      return fallback || (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-gray-900">Access Denied</h1>
            <p className="mt-2 text-gray-600">You don't have permission to access this resource.</p>
            <p className="mt-1 text-sm text-gray-500">Required: {module}.{action}</p>
          </div>
        </div>
      );
    }
  }

  // Module access check (any permission in module)
  if (module && !action) {
    if (!canAccessModule(module)) {
      return fallback || (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-gray-900">Access Denied</h1>
            <p className="mt-2 text-gray-600">You don't have permission to access this module.</p>
            <p className="mt-1 text-sm text-gray-500">Required module: {module}</p>
          </div>
        </div>
      );
    }
  }

  // Multiple permissions check
  if (permissions && permissions.length > 0) {
    const hasAccess = requireAll 
      ? hasAllPermissions(...permissions)
      : hasAnyPermission(...permissions);
      
    if (!hasAccess) {
      return fallback || (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-gray-900">Access Denied</h1>
            <p className="mt-2 text-gray-600">You don't have permission to access this resource.</p>
            <p className="mt-1 text-sm text-gray-500">
              Required {requireAll ? 'all' : 'any'} of: {permissions.join(', ')}
            </p>
          </div>
        </div>
      );
    }
  }

  return children;
};

export default PermissionGuard;